/**
 * Centrix Theme Configuration
 *
 * This will integrate with OpenEdX Paragon's theming system.
 */

import { colors, typography, spacing, borderRadius, shadows } from './tokens';

export interface ThemeConfig {
  primaryColor?: string;
  secondaryColor?: string;
  destructiveColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  foregroundColor?: string;
  radius?: string;
  logoUrl?: string;
}

/**
 * Create a customized theme based on company settings.
 */
export function createTheme(config: ThemeConfig = {}) {
  return {
    colors: {
      ...colors,
      primary: config.primaryColor
        ? {
            ...colors.primary,
            500: config.primaryColor,
            600: config.primaryColor,
          }
        : colors.primary,
      secondary: config.secondaryColor
        ? {
            ...colors.secondary,
            500: config.secondaryColor,
            600: config.secondaryColor,
          }
        : colors.secondary,
    },
    typography,
    spacing,
    borderRadius,
    shadows,
    logoUrl: config.logoUrl,
  };
}

/**
 * Default Centrix theme.
 */
export const defaultTheme = createTheme();

/**
 * CSS custom properties for the theme.
 */
export function getThemeCSSVars(config: ThemeConfig = {}): Record<string, string> {
  const theme = createTheme(config);

  const vars: Record<string, string> = {
    // shadcn-compatible semantic variables
    '--color-primary': config.primaryColor ?? theme.colors.primary[600],
    '--color-primary-foreground': colors.white,
    '--color-secondary': theme.colors.secondary[100],
    '--color-secondary-foreground': theme.colors.secondary[900],
    '--color-muted': theme.colors.secondary[100],
    '--color-muted-foreground': theme.colors.secondary[500],
    '--color-accent': config.accentColor ?? theme.colors.secondary[100],
    '--color-accent-foreground': theme.colors.secondary[900],
    '--color-destructive': config.destructiveColor ?? theme.colors.error.DEFAULT,
    '--color-destructive-foreground': config.destructiveColor ?? theme.colors.error.DEFAULT,
    '--color-background': config.backgroundColor ?? colors.white,
    '--color-foreground': config.foregroundColor ?? theme.colors.secondary[900],
    '--color-card': colors.white,
    '--color-card-foreground': theme.colors.secondary[900],
    '--color-popover': colors.white,
    '--color-popover-foreground': theme.colors.secondary[900],
    '--color-border': theme.colors.secondary[200],
    '--color-input': theme.colors.secondary[200],
    '--color-ring': config.primaryColor ?? theme.colors.primary[600],

    // Sidebar
    '--color-sidebar': theme.colors.secondary[50],
    '--color-sidebar-foreground': theme.colors.secondary[900],
    '--color-sidebar-primary': config.primaryColor ?? theme.colors.primary[600],
    '--color-sidebar-primary-foreground': colors.white,
    '--color-sidebar-accent': theme.colors.secondary[100],
    '--color-sidebar-accent-foreground': theme.colors.secondary[900],
    '--color-sidebar-border': theme.colors.secondary[200],
    '--color-sidebar-ring': config.primaryColor ?? theme.colors.primary[600],

    // Legacy aliases
    '--color-success': theme.colors.success.DEFAULT,
    '--color-warning': theme.colors.warning.DEFAULT,
    '--color-error': theme.colors.error.DEFAULT,
    '--color-info': theme.colors.info.DEFAULT,

    // Typography
    '--font-family-sans': theme.typography.fontFamily.sans,
    '--font-family-mono': theme.typography.fontFamily.mono,
  };

  if (config.radius) {
    vars['--radius'] = config.radius;
  }

  return vars;
}

/**
 * Apply theme CSS variables to document root.
 */
export function applyTheme(config: ThemeConfig = {}): void {
  const vars = getThemeCSSVars(config);
  const root = document.documentElement;

  Object.entries(vars).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
}
